'use client';

import { useEffect, useState } from 'react';

import {
    Box,
    Button,
    Columns,
    ColumnsItem,
    Icon,
    IconName,
    Price,
    PricingPlanHeader,
    Spacer,
    SpacerSize,
    Text,
    TextLevels,
    TypographyBold,
    View,
} from '@bytel/trilogy-react-ts';

import { OptionType, PlanType } from '@app-types/product';

import { classNames } from '@helpers/classnames';

import { OFFER_BLOCKED } from '@constants/app';

import { useHome } from '@landing/hooks';

import { BlockedProductOption } from './blocked-product-option';

interface ProductCatalogItemProps {
    product: PlanType;
    isHalfWidth: boolean;
    blockedOption?: OptionType;
}

export function ProductCatalogItem({
    product,
    isHalfWidth,
    blockedOption: _blockedOption,
}: Readonly<ProductCatalogItemProps>) {
    const { handleAddCatalogProductToCart, byouCart } = useHome();
    const [isOfferBlocked, setIsOfferBlocked] = useState(false);
    const blockedOption = _blockedOption ? [_blockedOption] : [];

    useEffect(() => {
        if (product.locked !== '1') return;
        const t = byouCart?.options?.find((o) => o.gencode === String(OFFER_BLOCKED));
        if (t) {
            setIsOfferBlocked(true);
        }
    }, [byouCart]);

    const handleCheck = () => {
        setIsOfferBlocked(!isOfferBlocked);
    };

    const onAddToCart = () => {
        let options: OptionType[] = [];
        if (product.locked === '1' && isOfferBlocked) {
            options = blockedOption;
        }

        handleAddCatalogProductToCart({ product, options });
    };
    return (
        <ColumnsItem size={isHalfWidth ? 6 : 12}>
            <div className='pricing-plan'>
                <div className='pricing-plan-product catalog-item has-background-transparent'>
                    <Box className='is-paddingless is-bordered has-background-transparent' shadowless>
                        <Box className='is-paddingless has-background-transparent' shadowless>
                            <PricingPlanHeader
                                className={classNames('small-card-header', 'has-background-secondary-20')}
                            >
                                <Columns verticalCentered marginless mobile>
                                    <ColumnsItem className='is-paddingless'>
                                        <Text level={TextLevels.TWO} marginless>
                                            Forfait B&YOU
                                        </Text>
                                        <Text typo={[TypographyBold.TEXT_WEIGHT_SEMIBOLD]} marginless>
                                            {product.dataEnvelope}
                                        </Text>
                                        <Text marginless level={TextLevels.FOUR}>
                                            sans engagement
                                        </Text>
                                    </ColumnsItem>
                                    <ColumnsItem narrow className='is-paddingless'>
                                        <View className='has-text-left'>
                                            <Price amount={product.price} level={5} period='mois' showCents />
                                        </View>
                                    </ColumnsItem>
                                </Columns>
                            </PricingPlanHeader>
                        </Box>
                        <Box shadowless className='is-radiusless'>
                            <View className='card-content'>
                                <View className='locked'>
                                    {product.locked === '1' && (
                                        <BlockedProductOption
                                            product={product}
                                            isOfferBlocked={isOfferBlocked}
                                            handleCheck={handleCheck}
                                            // setBlockedOption={setBlockedOption}
                                        />
                                    )}
                                </View>
                                <Spacer size={SpacerSize.MEDIUM} />
                                <ul className='icon-list has-text-left'>
                                    <li className='mb-3 mt-2'>
                                        <Icon
                                            name={IconName.AT}
                                            content={
                                                <Text level={TextLevels.THREE}>
                                                    <strong>{product.dataEnvelope} d&apos;internet</strong> dont{' '}
                                                    {product.roamingEnvelope || ' 0Go'} utilisables en Europe
                                                </Text>
                                            }
                                        />
                                    </li>
                                    <li className='mb-3'>
                                        <Icon
                                            name={IconName.PHONE_INFINITY}
                                            content={
                                                <Text level={TextLevels.THREE}>
                                                    Appels &amp; SMS illimités, MMS <strong>illimités</strong> en France
                                                    métropolitaine
                                                </Text>
                                            }
                                        />
                                    </li>
                                    <li className='mb-3'>
                                        <Icon
                                            name={IconName.SHOOTING_STAR}
                                            content={
                                                <Text level={TextLevels.THREE}>
                                                    <strong>
                                                        La qualité du réseau{' '}
                                                        {product.maxConnections === '5g' ? '5G' : '4G'}
                                                    </strong>{' '}
                                                    de Bouygues Telecom
                                                </Text>
                                            }
                                        />
                                    </li>
                                </ul>
                            </View>
                            <Button
                                className='is-primary is-fullwidth is-small has-byou-background'
                                onClick={onAddToCart}
                            >
                                Ajouter au panier
                            </Button>
                        </Box>
                    </Box>
                </div>
            </div>
        </ColumnsItem>
    );
}
